import { FC, useRef } from "react"

import { MANUAL_ACTIONS, RISK_LEVEL_CONFIG } from "components/risk/constants"

import dayjs from "libs/dayjs"

interface RiskScore {
  risk_level: number
  total_score: number
  financial_weighted: number
  profile_weighted: number
  behavioral_weighted: number
  updated_at: string
}

interface Props {
  riskScore: RiskScore
  onRecalculate: () => void
  onManualAction: (action: string) => void
  recalculating: boolean
}

export const RiskScoreHeader: FC<Props> = ({ riskScore, onRecalculate, onManualAction, recalculating }) => {
  const dropdownRef = useRef<HTMLDetailsElement>(null)
  const levelCfg = RISK_LEVEL_CONFIG[riskScore.risk_level] || RISK_LEVEL_CONFIG[1]

  const handleAction = (action: string) => {
    if (dropdownRef.current) dropdownRef.current.open = false
    onManualAction(action)
  }

  return (
    <div className="bg-white dark:bg-[#131421] border border-gray-200 dark:border-[rgba(255,255,255,0.1)] shadow-sm dark:shadow-none rounded-xl p-6">
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <span className={`badge ${levelCfg.badgeClass} badge-lg`}>{levelCfg.label}</span>
            <span className="text-3xl font-bold">{riskScore.total_score.toFixed(1)}</span>
            <span className="text-gray-500 dark:text-gray-400">/ 100</span>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Last calculated: {dayjs(riskScore.updated_at).format("DD.MM.YYYY h:mm A")}
          </p>
        </div>

        <div className="flex gap-2">
          <button className="btn btn-outline btn-sm" onClick={onRecalculate} disabled={recalculating}>
            {recalculating ? <span className="loading loading-spinner loading-xs" /> : null}
            Recalculate
          </button>

          <details ref={dropdownRef} className="dropdown dropdown-end">
            <summary className="btn btn-primary btn-sm">Actions ▾</summary>
            <ul className="dropdown-content menu p-2 shadow bg-base-300 rounded-box w-52 z-50">
              {MANUAL_ACTIONS.map((a) => (
                <li key={a.key} className={a.danger ? "text-error" : ""}>
                  <a onClick={() => handleAction(a.key)}>{a.label}</a>
                </li>
              ))}
            </ul>
          </details>
        </div>
      </div>

      <div className="my-4 border-t border-gray-200 dark:border-[rgba(255,255,255,0.1)]" />

      <div className="grid grid-cols-3 gap-4">
        <div>
          <p className="text-sm text-gray-500 dark:text-gray-400">Financial</p>
          <p className="text-lg font-semibold text-error">{riskScore.financial_weighted.toFixed(1)}</p>
        </div>
        <div>
          <p className="text-sm text-gray-500 dark:text-gray-400">Profile</p>
          <p className="text-lg font-semibold text-warning">{riskScore.profile_weighted.toFixed(1)}</p>
        </div>
        <div>
          <p className="text-sm text-gray-500 dark:text-gray-400">Behavioral</p>
          <p className="text-lg font-semibold text-accent">{riskScore.behavioral_weighted.toFixed(1)}</p>
        </div>
      </div>
    </div>
  )
}
